import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { UncontrolledTooltip } from 'reactstrap';
import FolderContainer from '../folders/folder-container';
import { DroppablePayloadTypes } from '../folders/folder-list';
import { moveFolder } from '../../services/folder';
import './side-bar.scss';
import '../common/tooltip.scss';
import { editNewMessage } from '../../services/application';
import {createFolder as createFolderAction} from '../../actions/application';
import { clearSelected } from '../../actions/messages';
import { selectMessage } from '../../actions/application';
import FolderNewIcon from '../../assets/icons/folder-new.svg?react';
import NewMessageIcon from '../../assets/icons/new.svg?react';
import PanelLeftIcon from '../../assets/icons/panel-left.svg?react';
import MailboxUsage from '../mailbox-usage/mailbox-usage';


const getCollapsedClassName = (collapsed) => {
  return collapsed
    ? 'd-flex flex-column sidebar-close-mail'
    : 'd-flex flex-column sidebar-open';
}

const SideBar = (props) => {
  const {
    collapsed,
    createFolder,
    lexon,
    messageClicked,
    clearListMessages,
    newMessage,
    moveFolderToFirstLevel,
    sideBarToggle
  } = props;
  const { t } = useTranslation();
  const [dragOver, setDragOver] = React.useState(false);

  const handleOnNewMessage = React.useCallback(() => {
    messageClicked(null);
    clearListMessages();
    newMessage(lexon.sign);
  }, [messageClicked, clearListMessages, newMessage, lexon]);

  const handleOnDragOver = React.useCallback((event) => {
    event.preventDefault();
    if (
      event.dataTransfer.types &&
      Array.from(event.dataTransfer.types).includes('application/json')
    ) {
      setDragOver(true);
    }
  }, []);

  const handleOnDragLeave = React.useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const handleOnDrop = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    if (
      event.dataTransfer.types &&
      Array.from(event.dataTransfer.types).includes('application/json')
    ) {
      const payload = JSON.parse(
        event.dataTransfer.getData('application/json')
      );
      if (payload.type === DroppablePayloadTypes.FOLDER) {
        moveFolderToFirstLevel(payload.folder);
      }
    }
  }, [moveFolderToFirstLevel]);

  return (
    <aside
      onDragOver={handleOnDragOver}
      onDragLeave={handleOnDragLeave}
      onDrop={handleOnDrop}
      className={`left-panel-mail
        ${getCollapsedClassName(collapsed)}
        ${dragOver ? 'dropZone' : ''}`}
    >
      <div className="d-flex justify-content-center">
        <div
          id="sidebar-toggle-btn"
          className={`
          d-flex
          align-items-center
          sidebar-toggle-container
          ${!collapsed ? 'sidebar-toggle-open' : 'sidebar-toggle-close'}
          `}
          onClick={sideBarToggle}
        >
          <PanelLeftIcon className="sidebar-toggle-icon" />
          {!collapsed && (
            <span className="inter-medium-14 text-color-grey-700 ml-2">
              {t('sideBar.hide')}
            </span>
          )}
        </div>
      </div>
      {collapsed && (
        <UncontrolledTooltip
          target="sidebar-toggle-btn"
          placement="right"
          popperClassName="lf-tooltip"
        >
          {t('topBar.showSideBar')}
        </UncontrolledTooltip>
      )}
      <div
        className={`
        d-flex
        justify-content-center
        ${!collapsed ? 'pl-4 pr-4 pt-4' : 'sidebar-close-p'}
        `}
      >
        <button
          className={
            !collapsed ? 'btn-compose' : 'btn-compose btn-compose-close'
          }
          onClick={handleOnNewMessage}
        >
          <NewMessageIcon />
          {!collapsed && (
            <span className="inter-semibold-12 text-color-white ml-1">
              {t('sideBar.compose')}
            </span>
          )}
        </button>
      </div>
      {!collapsed && (
        <div
          className={`
          d-flex
          create-folder-container
          justify-content-between
          align-items-center
          `}
        >
          <h3 className="inter-bold-14 text-color-grey-700">
            {t('general.general')}
          </h3>
          <div className="folder-new-action" onClick={createFolder}>
            <FolderNewIcon fill="var(--grey-800)" />
          </div>
        </div>
      )}
      <div className={`sidebar-scroll-container`}>
        <FolderContainer collapsed={collapsed} />
      </div>
      <MailboxUsage collapsed={collapsed} />
    </aside>
  );
}


SideBar.propTypes = {
  t: PropTypes.func,
  sideBarToggle: PropTypes.func.isRequired,
  collapsed: PropTypes.bool.isRequired,
  application: PropTypes.object,
  errors: PropTypes.object,
  newMessage: PropTypes.func.isRequired,
  lexon: PropTypes.object,
  messageClicked: PropTypes.func,
  clearListMessages: PropTypes.func,
  moveFolderToFirstLevel: PropTypes.func,
  createFolder: PropTypes.func
};

const mapStateToProps = state => ({
  application: state.application,
  errors: state.application.errors,
  lexon: state.lexon
});

const mapDispatchToProps = dispatch => ({
  moveFolderToFirstLevel: (user, folder) =>
    moveFolder(dispatch, user, folder, null),
  newMessage: sign => editNewMessage(dispatch, [], sign),
  messageClicked: message =>
    dispatch(selectMessage(message)),
  createFolder: () => dispatch(createFolderAction('')),
  clearListMessages: () => dispatch(clearSelected())
});

const mergeProps = (stateProps, dispatchProps, ownProps) =>
  Object.assign({}, stateProps, dispatchProps, ownProps, {
    moveFolderToFirstLevel: folder =>
      dispatchProps.moveFolderToFirstLevel(stateProps.application.user, folder),
    messageClicked: message => dispatchProps.messageClicked(message)
  });


const ConnectedSideBar = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(SideBar);

export default ConnectedSideBar;
